import { useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { Row } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { theme } from "../../../theme";
import BodySmall from "../../atoms/typography/BodySmall";
import TextTag from "../../atoms/TextTag";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Divider from "../../atoms/Divider";
import Subheading from "../../atoms/typography/Subheading";
import styled from "styled-components";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import type { InvoiceAssessmentModel } from "../../../models/InvoiceAssessment";
import {
  getInvoiceAssessmentStatusBadge,
  getInvoiceDistributionTypeTranslationKey,
  getInvoiceServiceTypeTranslationKey,
  getInvoiceTagTitleKey,
} from "../../../utils/invoicesHelpers";
import type { InvoiceTagModel } from "../../../models/InvoiceTags";
import dayjs from "dayjs";
import UpdateServiceTypeButton from "../../molecules/invoices/UpdateServiceTypeButton";
import DeliverApprovalStatusOnInvoice from "../../molecules/assessment/DeliverApprovalStatusOnInvoice";
import InvoiceDocumentTagCloud from "../../molecules/invoices/InvoiceDocumentTagCloud";
import Modal from "../../molecules/Modal";
import DuplicatedInvoiceNumberListModalContent from "../../molecules/modals/invoices/DuplicatedInvoiceNumberListModalContent";
import TextLinkButton from "../../atoms/buttons/TextLinkButton";
import SendInvoiceBackToValidationButton from "../../molecules/invoices/SendInvoiceBackToValidationButton";
import Alert from "../../molecules/Alert";
import TextButton from "../../atoms/buttons/TextButton";
import { InvoiceAssessmentRoute } from "../../../utils/routes";
import Checkbox from "../../atoms/inputs/Checkbox";
import Small from "../../atoms/typography/Small";
import { useSetManualReviewRequired } from "../../../graphql/hooks/invoices/useSentManualReviewRequired";
import CopyForDebuggingButton from "../../molecules/CopyForDebuggingButton";

interface Props {
  invoice?: InvoiceAssessmentModel;
  clientIdentifier?: string;
  isEditable: boolean;
}

const InfoRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0px;
`;

const DupeButtonContainer = styled.button`
  border: none;
  background-color: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;

  &:hover {
    filter: opacity(0.6);
  }
`;

const InvoiceDetailsOverview: FC<Props> = ({
  invoice,
  clientIdentifier,
  isEditable,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [invoiceDetailsOpen, setInvoiceDetailsOpen] = useState<boolean>(false);
  const [duplicatedModalOpen, setDuplicatedModalOpen] = useState(false);

  const [successfullySentToValidation, setSuccessfullySentToValidation] =
    useState<boolean>(false);

  const [
    invoiceManualCorrectionsUpdatedSuccessfully,
    setInvoiceManualCorrectionsUpdatedSuccessfully,
  ] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");

  const onCompletedManualCorrections = (res?: any) => {
    if (res?.invoice?.setManualReviewRequired?.success) {
      setErrorMessage("");
      setInvoiceManualCorrectionsUpdatedSuccessfully(true);
      setTimeout(
        () => setInvoiceManualCorrectionsUpdatedSuccessfully(false),
        4000,
      );
    } else {
      setErrorMessage(t("invoiceAssessment.manualCorrectionsFailed"));
    }
  };
  const onErrorManualCorrections = (e?: any) => {
    setErrorMessage(
      e?.message ?? t("invoiceAssessment.manualCorrectionsFailed"),
    );
  };

  const { setManualReviewRequired, error, loading } =
    useSetManualReviewRequired({
      onCompleted: onCompletedManualCorrections,
      onError: onErrorManualCorrections,
    });

  const onUpdateManualCorrections = async (manualReviewRequired: boolean) => {
    try {
      await setManualReviewRequired({
        variables: {
          input: {
            invoiceId: invoice?.id,
            manualReviewRequired: manualReviewRequired,
          },
        },
      });
    } catch (e) {
      onErrorManualCorrections(e);
    }
  };

  return (
    <div>
      <InfoRow>
        <BodySmall>{t("common.status")}</BodySmall>
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {invoice?.state?.assessmentStatus &&
            getInvoiceAssessmentStatusBadge(invoice.state.assessmentStatus)}
          {invoice?.tags?.map((tag: InvoiceTagModel) => (
            <TextTag
              key={tag.invoiceTagId}
              tagTitleKey={getInvoiceTagTitleKey(tag.tag)}
            />
          ))}
          {invoice?.hasDuplicateInvoices && (
            <DupeButtonContainer
              onClick={() => {
                setDuplicatedModalOpen(true);
              }}
            >
              <TextTag tagTitleKey="DUPE" variant="warning" />
            </DupeButtonContainer>
          )}
          {invoice?.isCreditInvoice && (
            <TextTag tagTitleKey="Kreditfaktura" variant="pink" />
          )}
        </div>
      </InfoRow>

      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />
      <div
        style={{
          display: "flex",
          gap: 32,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {invoice?.id && (
          <SendInvoiceBackToValidationButton
            variant="textButton"
            invoiceId={invoice?.id}
            onSuccess={() => setSuccessfullySentToValidation(true)}
            disabled={!isEditable}
          />
        )}
        {invoice?.id && (
          <CopyForDebuggingButton
            copyValue={invoice?.id}
            textValue="Fakturaid"
            variant="discrete"
          />
        )}
      </div>
      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />
      <Subheading color={theme.colors.primary}>
        {t("invoices.manualCorrections")}
      </Subheading>
      <HorizontalSpacer spacing={0.25} />
      <Small fontweight={400} color={theme.colors.textSecondary}>
        {t("invoices.manualCorrectionsDescription")}
      </Small>
      <HorizontalSpacer spacing={0.25} />
      <Checkbox
        title={t("invoices.manualCorrectionsRequired")}
        checked={invoice?.latestAssessment?.manualReviewRequired ?? false}
        onChange={() =>
          onUpdateManualCorrections(
            invoice?.latestAssessment?.manualReviewRequired ? false : true,
          )
        }
        loading={loading}
        disabled={!isEditable}
      />
      {(error || errorMessage) && (
        <Alert
          type="error"
          title={errorMessage || t("invoices.manualCorrectionsError")}
          message={errorMessage}
          small
        />
      )}

      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />
      <Subheading color={theme.colors.primary}>
        {t("invoiceAssessment.distribution")}
      </Subheading>
      <HorizontalSpacer spacing={0.5} />
      <InfoRow>
        <BodySmall>{t("invoiceAssessment.deliveryMethod")}</BodySmall>
        <BodySmall fontweight={500}>
          {t(
            getInvoiceDistributionTypeTranslationKey(
              invoice?.invoiceRecipient?.distributionType,
            ),
          )}
        </BodySmall>
      </InfoRow>

      <InfoRow>
        <BodySmall>{t("invoiceAssessment.edi")}</BodySmall>
        <BodySmall fontweight={500}>
          {invoice?.invoiceRecipient?.ediAddress ?? "-"}
        </BodySmall>
      </InfoRow>

      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />
      <InfoRow>
        <BodySmall>{t("invoices.template")}</BodySmall>
        <TextLinkButton
          title={invoice?.template?.name ?? ""}
          onClick={() => {
            navigate(
              `/client/${clientIdentifier}/invoices/templates/edit/${invoice?.template?.id}`,
            );
          }}
        />
      </InfoRow>
      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />
      <div>
        {invoice?.uploadDocument && (
          <InvoiceDocumentTagCloud
            documentRegistrationDetails={invoice.uploadDocument}
            titleComponent={
              <Subheading color={theme.colors.primary}>
                {t("invoiceAssessment.fromUpload")}
              </Subheading>
            }
            expandable
          />
        )}
      </div>
      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />
      <Row nogutter align="center" justify="between">
        <Subheading color={theme.colors.primary}>
          {t("invoices.invoiceDetails")}
        </Subheading>
        <ChevronButton
          type={invoiceDetailsOpen ? "up" : "down"}
          onClick={() => setInvoiceDetailsOpen(!invoiceDetailsOpen)}
        />
      </Row>
      <SlideOutHeight isVisible={invoiceDetailsOpen}>
        <HorizontalSpacer spacing={0.75} />
        <InfoRow>
          <BodySmall>{t("invoices.invoiceNumber")}</BodySmall>
          <BodySmall fontweight={500}>
            {invoice?.invoiceNumber ?? "-"}
          </BodySmall>
        </InfoRow>
        <InfoRow>
          <BodySmall>{t("invoiceAssessment.terms")}</BodySmall>
          <BodySmall fontweight={500}>
            {invoice?.dueDate && invoice?.invoiceDate
              ? dayjs(invoice.dueDate).diff(dayjs(invoice.invoiceDate), "days")
              : "-"}{" "}
            {t("dates.days").toLowerCase()}
          </BodySmall>
        </InfoRow>
        <InfoRow>
          <BodySmall>{t("invoices.invoiceDate")}</BodySmall>
          <BodySmall fontweight={500}>
            {invoice?.invoiceDate
              ? dayjs(invoice.invoiceDate).format("YYYY-MM-DD")
              : "-"}
          </BodySmall>
        </InfoRow>
        <InfoRow>
          <BodySmall>{t("invoices.dueDate")}</BodySmall>
          <BodySmall fontweight={500}>
            {invoice?.dueDate
              ? dayjs(invoice.dueDate).format("YYYY-MM-DD")
              : "-"}
          </BodySmall>
        </InfoRow>
      </SlideOutHeight>

      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />
      <Subheading color={theme.colors.primary}>
        {t("invoiceAssessment.manageDetails")}
      </Subheading>
      <InfoRow>
        <BodySmall>{t("invoiceAssessment.serviceType")}</BodySmall>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <BodySmall fontweight={500}>
            {t(
              getInvoiceServiceTypeTranslationKey(
                invoice?.serviceType ?? "NONE",
              ),
            )}
          </BodySmall>
          {invoice?.serviceType && invoice.id && (
            <UpdateServiceTypeButton
              invoiceId={invoice?.id}
              currentServiceType={invoice?.serviceType}
              buttonType="circleOutlined"
              disabled={!isEditable}
            />
          )}
        </div>
      </InfoRow>
      {invoice?.id && clientIdentifier && (
        <DeliverApprovalStatusOnInvoice
          invoiceId={invoice.id}
          clientIdentifier={clientIdentifier}
          disabled={!isEditable}
        />
      )}
      <HorizontalSpacer spacing={1} />
      {clientIdentifier && invoice?.id && duplicatedModalOpen && (
        <Modal
          onClose={() => {
            setDuplicatedModalOpen(false);
          }}
          closeIcon
          width={1250}
        >
          <DuplicatedInvoiceNumberListModalContent
            clientIdentifier={clientIdentifier}
            invoiceId={invoice?.id}
          />
        </Modal>
      )}
      {successfullySentToValidation && (
        <Alert
          type="success"
          title={t("invoiceAssessment.invoiceSuccessfullyReturnedToValidation")}
          version="snackbar"
          small
          onClickClose={() => setSuccessfullySentToValidation(false)}
          message={
            <div>
              <TextButton
                title={t("invoiceValidation.openInValidation")}
                onClick={() =>
                  navigate(
                    `/invoices/validate/${invoice?.id}/${clientIdentifier}`,
                    { state: { from: { routeId: InvoiceAssessmentRoute.id } } },
                  )
                }
              />
            </div>
          }
        />
      )}

      {invoiceManualCorrectionsUpdatedSuccessfully && (
        <Alert
          type="success"
          title={t("invoiceAssessment.manualCorrectionsUpdatedSuccessfully")}
          version="snackbar"
          small
        />
      )}
    </div>
  );
};

export default InvoiceDetailsOverview;
